import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FileInputComponent } from '../../../shared/file-input/file-input.component';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { DateService } from '../../../shared/services/date.service';
import { RSJService } from '../../../shared/services/rsj.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { Utils } from '../../../shared/utils';
import { DocumentDto } from '../../beneficiary-details/documents/document.dto';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { RsjPaymentDataDto } from '../dto/rsj-payment-data.dto';
import { RsjDto } from '../dto/rsj.dto';
import { RsjPaymentInformationComponent } from './rsj-payment-information/rsj-payment-information.component';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';

@Component({
  selector: 'app-rsj-payment',
  templateUrl: './rsj-payment.component.html',
  providers: [DatePipe],
})
export class RsjPaymentComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject();

  @ViewChild(FileInputComponent) public ribInput: FileInputComponent;
  @ViewChild(RsjPaymentInformationComponent, { static: false }) paymentsComponent: RsjPaymentInformationComponent;

  public paymentDataForm: UntypedFormGroup;
  public beneficiary: BeneficiaryDto;
  public rsj: RsjDto;
  public paymentData: RsjPaymentDataDto;
  public historizedData: RsjPaymentDataDto[] = [];
  public lastSavedFile: File;
  public currentFileId: number;
  public submitted: boolean = false;
  public hasPayments: boolean = false;
  private currentDate: Date;
  private currentUser: UserDto;

  public maxFileSize: number = environment.UPLOAD_MAX_FILESIZE ?? 1000 * 1000 * 1.2;
  public maxFileSizeStr: string = Utils.humanFileSize(this.maxFileSize, true, 2);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly rsjService: RSJService,
    private readonly toastService: ToastService,
    private readonly dateService: DateService,
    public datepipe: DatePipe,
    public readonly authService: AuthService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.currentDate = this.dateService.getCurrent();
    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));
    this.currentUser = await this.authService.getUser();

    await this.loadPaymentData();
  }

  private async loadPaymentData(): Promise<void> {
    if (this.beneficiary?.id) {
      this.paymentData = await this.rsjService.getPaymentData(this.beneficiary.id);
      this.hasPayments = this.paymentData?.hasPayments || false;
      this.initForm();
      this.currentFileId = this.paymentData?.pdfId;
    }
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.paymentDataForm.valid) {
      const paymentDataToSave: RsjPaymentDataDto = {
        id: this.paymentDataForm.controls.id.value || undefined,
        beginDate: this.paymentDataForm.controls.beginDate.value,
        beneficiaryId: this.beneficiary.id,
        holderFirstName: this.paymentDataForm.controls.holderFirstName.value.toUpperCase(),
        holderLastName: this.paymentDataForm.controls.holderLastName.value.toUpperCase(),
        bankName: this.paymentDataForm.controls.bankName.value.toUpperCase(),
        iban: this.formatIban(this.paymentDataForm.controls.iban.value),
        bic: this.paymentDataForm.controls.bic.value.toUpperCase(),
        livretA: this.paymentDataForm.controls.livretA.value,
        pdfId: this.currentFileId,
        pdfName: this.paymentDataForm.controls.pdf.value?.fileName,
        pdfData: this.paymentDataForm.controls.pdf.value?.fileData || '',
        historizedPaymentData: [...this.historizedData],
        visa: this.paymentDataForm.controls.visa.value,
      } as RsjPaymentDataDto;

      await this.rsjService.savePaymentData(paymentDataToSave);
      this.toastService.show('Les informations bancaires du bénéficiaire ont été sauvegardées avec succès.', 'success');
      this.paymentData = paymentDataToSave;
      this.lastSavedFile = this.ribInput?.file;
      this.submitted = false;
      await this.paymentsComponent.ngOnInit();
      await this.loadPaymentData();
    } else {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    }
  }

  public cancel(): void {
    this.initForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public formatUppercaseInput(event: any): void {
    if (event?.target?.value) {
      event.target.value = (event.target.value as string).toUpperCase();
    }
  }

  public formatIbanInput(event: any): void {
    if (event?.target?.value) {
      event.target.value = this.formatIban(event.target.value as string);
    }
  }

  private formatIban(iban: string): string {
    return iban
      .toUpperCase()
      .replaceAll(' ', '')
      .match(/.{1,4}/g)
      .join(' ');
  }

  public async historize(): Promise<void> {
    this.submitted = true;
    if (this.paymentDataForm.disabled || this.paymentDataForm.valid) {
      this.historizedData = [
        {
          id: this.paymentDataForm.controls.id.value || undefined,
          beginDate: this.paymentDataForm.controls.beginDate.value,
          endDate: this.currentDate,
          beneficiaryId: this.beneficiary.id,
          holderFirstName: this.paymentDataForm.controls.holderFirstName.value.toUpperCase(),
          holderLastName: this.paymentDataForm.controls.holderLastName.value.toUpperCase(),
          bankName: this.paymentDataForm.controls.bankName.value.toUpperCase(),
          iban: this.formatIban(this.paymentDataForm.controls.iban.value),
          bic: this.paymentDataForm.controls.bic.value.toUpperCase(),
          livretA: this.paymentDataForm.controls.livretA.value,
          pdfId: this.paymentDataForm.controls.pdf.value?.id,
          pdfName: this.paymentDataForm.controls.pdf.value?.fileName,
          pdfData: this.paymentDataForm.controls.pdf.value?.fileData,
          hasPayments: this.hasPayments,
          visa: this.paymentDataForm.controls.visa.value,
        } as RsjPaymentDataDto,
      ].concat(this.historizedData);
      this.paymentDataForm.reset();
      this.paymentDataForm.enable();
      if (this.currentUser?.groups?.includes('STRUCTURE')) {
        this.paymentDataForm.controls.visa.disable();
      }
      this.paymentDataForm.controls.livretA.setValue(false);
      this.paymentDataForm.controls.beginDate.setValue(this.currentDate);
      this.paymentDataForm.controls.holderFirstName.setValue(Utils.unaccent(this.beneficiary.firstName).toUpperCase());
      this.paymentDataForm.controls.holderLastName.setValue(Utils.unaccent(this.beneficiary.usualName).toUpperCase());
      this.paymentDataForm.controls.visa.setValue(false);
      this.currentFileId = undefined;
      this.hasPayments = false;
      this.submitted = false;
    }
  }

  public deleteHistory(history: RsjPaymentDataDto): void {
    this.historizedData = this.historizedData.filter((_history) => _history !== history);
  }

  private initForm(): void {
    this.paymentDataForm = this.fb.group({
      id: [this.paymentData?.id],
      beginDate: [this.paymentData?.beginDate || this.currentDate],
      holderFirstName: [
        this.paymentData?.holderFirstName || Utils.unaccent(this.beneficiary.firstName).toUpperCase(),
        [Validators.required, Validators.pattern(Utils.lettersPattern)],
      ],
      holderLastName: [
        this.paymentData?.holderLastName || Utils.unaccent(this.beneficiary.usualName).toUpperCase(),
        [Validators.required, Validators.pattern(Utils.lettersPattern)],
      ],
      bankName: [this.paymentData?.bankName, [Validators.required, Validators.pattern(Utils.lettersPattern)]],
      iban: [this.paymentData?.iban, [Validators.required]],
      bic: [this.paymentData?.bic, [Validators.required]],
      livretA: [this.paymentData?.livretA || false, [Validators.required]],
      visa: [this.paymentData?.visa || false, [Validators.required]],
      pdf: [
        this.paymentData
          ? ({
              id: this.paymentData.pdfId,
              fileName: this.paymentData.pdfName,
            } as DocumentDto)
          : null,
        [Validators.required],
      ],
    });
    this.historizedData = this.paymentData?.historizedPaymentData
      ? [...this.paymentData.historizedPaymentData].sort((a: RsjPaymentDataDto, b: RsjPaymentDataDto) => (a.beginDate < b.beginDate ? 1 : -1))
      : [];
    if (this.hasPayments) {
      this.paymentDataForm.disable();
    }
    this.historizedData = this.paymentData?.historizedPaymentData
      ? [...this.paymentData.historizedPaymentData].sort((a: RsjPaymentDataDto, b: RsjPaymentDataDto) => (a.beginDate < b.beginDate ? 1 : -1))
      : [];

    if (this.currentUser?.groups?.includes('STRUCTURE')) {
      // and a RIB existsthe entry is deactivated
      if (this.paymentData?.id) {
        this.paymentDataForm.disable();
      } else {
        this.paymentDataForm.controls.visa.disable();
      }
    }
  }

  private resetForm(): void {
    this.paymentDataForm.patchValue({
      id: this.paymentData?.id,
      beginDate: this.paymentData?.beginDate || this.currentDate,
      holderFirstName: this.paymentData?.holderFirstName,
      holderLastName: this.paymentData?.holderLastName,
      bankName: this.paymentData?.bankName,
      iban: this.paymentData?.iban,
      bic: this.paymentData?.bic,
      livretA: this.paymentData?.livretA || false,
      visa: this.paymentData?.visa || false,
      pdf: this.paymentData
        ? ({
            id: this.paymentData.pdfId,
            fileName: this.paymentData.pdfName,
          } as DocumentDto)
        : null,
    });
    this.historizedData = this.paymentData?.historizedPaymentData
      ? [...this.paymentData.historizedPaymentData].sort((a: RsjPaymentDataDto, b: RsjPaymentDataDto) => (a.beginDate < b.beginDate ? 1 : -1))
      : [];
    if (this.hasPayments) {
      this.paymentDataForm.disable();
    }
  }
}
